import PropTypes from "prop-types";
import React, { useState, useCallback, useMemo } from "react";
import { FiPlay, FiPlus, FiCheck, FiChevronDown, FiSearch, FiClock } from "react-icons/fi";

const WordBank = ({
  words,
  userLists: initialUserLists,
  languageName,
  languageCode,
  languageKey,
  isAuthenticated,
  signUpPath,
  newListPath,
  wordsRemaining: initialWordsRemaining,
  csrfToken,
}) => {
  const [userLists, setUserLists] = useState(initialUserLists || []);
  const [addedWords, setAddedWords] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [wordsRemaining, setWordsRemaining] = useState(initialWordsRemaining);
  const [loadingWord, setLoadingWord] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredWords = useMemo(() => {
    if (!searchQuery.trim()) return words;
    const q = searchQuery.toLowerCase().trim();
    return words.filter(
      (w) =>
        w.wordText.toLowerCase().includes(q) ||
        (w.englishText && w.englishText.toLowerCase().includes(q)),
    );
  }, [words, searchQuery]);

  const playAudio = useCallback((url) => {
    if (!url) return;
    const audio = new Audio(url);
    audio.play().catch((err) => console.error("Audio error:", err));
  }, []);

  const addWordToList = async (wordId, listId) => {
    setLoadingWord(wordId);
    setOpenDropdown(null);

    try {
      const response = await fetch(`/api/v1/word_lists/${listId}/add_word`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ word_id: wordId }),
      });

      const data = await response.json();

      if (response.ok) {
        setAddedWords((prev) => ({ ...prev, [wordId]: listId }));
        if (data.wordsRemaining !== undefined) {
          setWordsRemaining(data.wordsRemaining);
        }
      } else {
        alert(data.error || "Could not add word.");
      }
    } catch (err) {
      console.error("Error adding word:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoadingWord(null);
    }
  };

  const listName = (listId) => {
    const list = userLists.find((l) => l.id === listId);
    return list ? list.name : "";
  };

  return (
    <div>
      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search in English or ${languageName}...`}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white shadow-sm text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-xs text-gray-400 mt-2">
            {filteredWords.length} {filteredWords.length === 1 ? "word" : "words"} found
          </p>
        )}
      </div>

      {/* Words Remaining Banner */}
      {isAuthenticated && wordsRemaining !== null && (
        <div className="mb-6 bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm text-gray-700">
            <strong className="text-indigo-600">{wordsRemaining}</strong> free
            words remaining this week
          </span>
          {wordsRemaining <= 0 && (
            <a
              href="/pricing"
              className="text-sm text-indigo-600 font-medium hover:text-indigo-800"
            >
              Upgrade for unlimited →
            </a>
          )}
        </div>
      )}

      {/* Word Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredWords.map((word) => {
          const isAdded = !!addedWords[word.id];
          const isLoading = loadingWord === word.id;

          return (
            <div
              key={word.id}
              className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${
                isAdded
                  ? "border-green-200 bg-green-50/30"
                  : "border-gray-100 hover:shadow-md hover:border-indigo-200"
              }`}
            >
              <div className="flex">
                {/* Image thumbnail */}
                {word.imageUrl && (
                  <div className="w-20 h-20 flex-shrink-0 bg-gray-100">
                    <img
                      src={word.imageUrl}
                      alt={word.englishText || word.wordText}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Word content — bilingual display */}
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-bold text-gray-800 text-lg leading-tight truncate">
                        {word.wordText}
                      </h3>
                      {word.englishText && word.englishText !== word.wordText && (
                        <p className="text-sm text-indigo-500 font-medium">
                          {word.englishText}
                        </p>
                      )}
                    </div>

                    {/* Audio buttons */}
                    {word.audioWordUrl && (
                      <div className="flex-shrink-0 flex items-center space-x-1">
                        <button
                          onClick={() => playAudio(word.audioWordUrl)}
                          className="p-1.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-full transition"
                          title="Listen"
                        >
                          <FiPlay size={14} />
                        </button>
                        <button
                          onClick={() => playAudio(word.audioWordUrl, 0.75)}
                          className="p-1.5 text-gray-400 hover:text-indigo-700 hover:bg-indigo-50 rounded-full transition"
                          title="Listen slowly"
                        >
                          <FiClock size={14} />
                        </button>
                      </div>
                    )}
                  </div>

                  {word.sentence && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2 italic">
                      {word.sentence}
                    </p>
                  )}
                </div>
              </div>

              {/* Add to list footer */}
              <div className="border-t border-gray-50 px-3 py-2 bg-gray-50/50">
                {isAdded ? (
                  <div className="flex items-center text-green-600 text-xs font-medium">
                    <FiCheck className="mr-1" size={12} />
                    Added to {listName(addedWords[word.id])}
                  </div>
                ) : !isAuthenticated ? (
                  <a
                    href={signUpPath}
                    className="flex items-center text-indigo-600 text-xs font-medium hover:text-indigo-800"
                  >
                    <FiPlus className="mr-1" size={12} />
                    Sign up to add to your list
                  </a>
                ) : userLists.length === 0 ? (
                  <a
                    href={newListPath}
                    className="flex items-center text-indigo-600 text-xs font-medium hover:text-indigo-800"
                  >
                    <FiPlus className="mr-1" size={12} />
                    Create a list to add words
                  </a>
                ) : isLoading ? (
                  <div className="flex items-center text-gray-400 text-xs">
                    <svg
                      className="animate-spin mr-1 h-3 w-3"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Adding...
                  </div>
                ) : wordsRemaining !== null && wordsRemaining <= 0 ? (
                  <a
                    href="/pricing"
                    className="flex items-center text-gray-400 text-xs"
                  >
                    Weekly limit reached · Upgrade
                  </a>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === word.id ? null : word.id,
                        )
                      }
                      className="flex items-center text-indigo-600 text-xs font-medium hover:text-indigo-800 w-full"
                    >
                      <FiPlus className="mr-1" size={12} />
                      Add to my list
                      <FiChevronDown className="ml-auto" size={12} />
                    </button>

                    {openDropdown === word.id && (
                      <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 max-h-40 overflow-y-auto">
                        {userLists.map((list) => (
                          <button
                            key={list.id}
                            onClick={() => addWordToList(word.id, list.id)}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 truncate"
                          >
                            {list.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* No results message */}
      {searchQuery && filteredWords.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No words match "{searchQuery}"</p>
          <button
            onClick={() => setSearchQuery("")}
            className="mt-2 text-indigo-600 text-sm hover:text-indigo-800"
          >
            Clear search
          </button>
        </div>
      )}
    </div>
  );
};

WordBank.propTypes = {
  words: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      wordText: PropTypes.string.isRequired,
      sentence: PropTypes.string,
      audioWordUrl: PropTypes.string,
      englishText: PropTypes.string,
      imageUrl: PropTypes.string,
    }),
  ).isRequired,
  userLists: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  languageName: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  languageKey: PropTypes.string.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  signUpPath: PropTypes.string,
  newListPath: PropTypes.string,
  wordsRemaining: PropTypes.number,
  csrfToken: PropTypes.string.isRequired,
};

export default WordBank;
